/**
 * Hook per lista chat con infinite scroll e ricerca avanzata
 */

import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import { useWhatsAppChatMetaStore } from '../../stores/whatsappChatMetaStore';
import { useWhatsAppAgentStore } from '../../stores/whatsappAgentStore';
import { useWhatsAppSearch } from './useWhatsAppSearch';
import { supabase } from '../../supabase';
import { arrangeChats, hasRecentActivity } from '../../services/whatsapp/chatListRules';
import { useChatRecontactStore } from '../../stores/chatRecontactStore';
import { recontactStatus, withDueRecontactsOnTop } from '../../services/whatsapp/recontactRules';

/** Tetto di pagine per "Carica tutte" (500 chat a pagina): oltre, meglio la ricerca. */
const LOAD_ALL_MAX_PAGES = 20;

/**
 * @param options.showArchived true = mostra solo le archiviate (sezione "Archiviate").
 * Con una ricerca in corso le archiviate compaiono insieme alle altre, come su WhatsApp.
 */
export function useWhatsAppChats(externalSearchQuery?: string, options?: { showArchived?: boolean }) {
  const showArchived = !!options?.showArchived;
  const {
    chats,
    chatsLoading,
    chatsError,
    chatsHasMore,
    chatsLoadingMore,
    selectedChatId,
    wahaConfig,
    loadChats,
    loadMoreChats,
    refreshChats,
    selectChat,
  } = useWhatsAppChatStore();

  const { chatMeta, chatLabels, filters } = useWhatsAppChatMetaStore();
  const { assignments } = useWhatsAppAgentStore();
  const reminders = useChatRecontactStore((s) => s.reminders);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id || null);
    });
  }, []);

  // Ricerca avanzata con fallback API
  const { results: searchResults, isSearching, clientsWithoutChat } = useWhatsAppSearch(externalSearchQuery || '');

  // Safe chats array
  const safeChats = chats || [];

  // NOTA: loadChats() è già chiamato da WhatsApp2Page dopo loadConfig().
  // NON chiamarlo qui per evitare doppia chiamata API.

  // Se c'è query, usa searchResults, altrimenti tutte le chat. Poi applica filtri avanzati.
  const { filteredChats, archivedCount } = useMemo(() => {
    const searching = !!externalSearchQuery?.trim();
    let list = searching ? searchResults : safeChats;
    const now = Date.now();

    if (filters.status) {
      list = list.filter((c) => {
        const meta = chatMeta.get(c.id);
        const ok = (meta?.conversation_status || 'da_gestire') === filters.status;
        // "Da gestire" solo per chat vive: senza attivita' da 30 giorni non sono lavoro da fare.
        return ok && (filters.status !== 'da_gestire' || hasRecentActivity(c, now));
      });
    }

    if (filters.groupsOnly) {
      list = list.filter((c) => c.isGroup || c.id.endsWith('@g.us'));
    }

    if (filters.labelId) {
      list = list.filter((c) => {
        const labels = chatLabels.get(c.id) || [];
        return labels.some((l) => l.id === filters.labelId);
      });
    }

    if (filters.myChatsOnly && currentUserId) {
      list = list.filter((c) => {
        const meta = chatMeta.get(c.id);
        return meta?.assigned_operator_id === currentUserId;
      });
    }

    if (filters.myProjectsOnly && currentUserId) {
      list = list.filter((c) => {
        const assignment = assignments.find(a => a.chat_jid === c.id);
        return assignment?.project?.project_manager_id === currentUserId;
      });
    }

    if (filters.unreadOnly) {
      list = list.filter((c) => (c.unreadCount || 0) > 0);
    }

    const isDue = (c: (typeof list)[number]) => recontactStatus(reminders.get(c.id), c, now).due;
    if (filters.recontactOnly) {
      list = list.filter(isDue);
    }

    // Da ricontattare e fissate in cima, archiviate a parte (in ricerca si vedono tutte).
    const arranged = arrangeChats(list, (id) => chatMeta.get(id));
    const { visible, archived } = withDueRecontactsOnTop(arranged.visible, arranged.archived, isDue);
    if (searching) return { filteredChats: [...visible, ...archived], archivedCount: archived.length };
    return { filteredChats: showArchived ? archived : visible, archivedCount: archived.length };
  }, [externalSearchQuery, searchResults, safeChats, filters, chatMeta, chatLabels, currentUserId, assignments, showArchived, reminders]);

  // "Carica tutte": pagine successive finche' WAHA ne ha (i filtri lavorano sulle chat caricate).
  const [loadingAll, setLoadingAll] = useState(false);
  const loadingAllRef = useRef(false);
  const loadAll = useCallback(async () => {
    if (loadingAllRef.current) return;
    loadingAllRef.current = true;
    setLoadingAll(true);
    try {
      for (let i = 0; i < LOAD_ALL_MAX_PAGES; i++) {
        const st = useWhatsAppChatStore.getState();
        if (!st.chatsHasMore) break;
        if (st.chatsLoadingMore) { await new Promise((r) => setTimeout(r, 400)); continue; }
        const before = st.chats.length;
        await st.loadMoreChats();
        const after = useWhatsAppChatStore.getState();
        if (after.chats.length === before && after.chatsHasMore) break; // nessun progresso: non girare a vuoto
      }
    } finally {
      loadingAllRef.current = false;
      setLoadingAll(false);
    }
  }, []);

  // Chat selezionata
  const selectedChat = useMemo(
    () => safeChats.find((c) => c.id === selectedChatId) || null,
    [safeChats, selectedChatId]
  );

  const handleSelectChat = useCallback(
    (chatId: string) => {
      selectChat(chatId);
    },
    [selectChat]
  );

  // Refresh con loading indicator
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshChats();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshChats]);

  return {
    chats: safeChats,
    filteredChats,
    allChats: safeChats,
    loading: chatsLoading,
    loadingMore: chatsLoadingMore,
    hasMore: chatsHasMore && !externalSearchQuery, // Disabilita scroll se cerca
    /** Ci sono altre chat su WAHA non ancora caricate (i filtri vedono solo quelle caricate). */
    moreOnServer: chatsHasMore,
    loadAll,
    loadingAll,
    archivedCount,
    clientsWithoutChat,
    isSearching,
    isRefreshing,
    error: chatsError,
    selectedChatId,
    selectedChat,
    selectChat: handleSelectChat,
    loadMore: loadMoreChats,
    refresh,
  };
}
